"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Card, CardContent } from "../card.js";
import { Alert, AlertDescription } from "../alert.js";
import { Loader2, AlertCircle } from "lucide-react";

interface OAuthIntermediatePageProps {
  /**
   * The base path for redirects (e.g., "/unifiedtime" or "/crm/calendar")
   */
  basePath: string;
  /**
   * The target page to redirect to after processing OAuth (e.g., "google", "microsoft")
   * If not provided, will auto-detect from URL parameters
   */
  targetPage?: string;
  /**
   * Optional custom error message
   */
  customErrorMessage?: string;
  /**
   * Optional custom loading message
   */
  customLoadingMessage?: string;
}

export default function OAuthIntermediatePage({
  basePath,
  targetPage,
  customErrorMessage = "Authorization code not found. Please try again.",
  customLoadingMessage = "Processing authentication..."
}: OAuthIntermediatePageProps) {
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = () => {
      // Read from query string (Google) and hash fragment (Microsoft)
      const queryCode = searchParams?.get("code");
      const queryState = searchParams?.get("state");
      const queryError = searchParams?.get("error");
      const scope = searchParams?.get("scope");

      const hashParams = new URLSearchParams(
        typeof window !== "undefined"
          ? window.location.hash.replace(/^#/, "")
          : ""
      );
      const hashCode = hashParams.get("code");
      const hashState = hashParams.get("state");
      const hashError = hashParams.get("error");

      const errorParam = queryError || hashError;
      if (errorParam) {
        setError(decodeURIComponent(errorParam));
        setIsLoading(false);
        return;
      }

      // Prefer query params; fallback to hash fragment
      const code = queryCode || hashCode;
      const state = queryState || hashState || "";

      if (code) {
        // Auto-detect OAuth provider if targetPage is not provided
        let detectedTargetPage = targetPage;
        
        if (!detectedTargetPage) {
          // Detect provider based on scope parameter
          if (scope) {
            if (scope.includes('googleapis.com')) {
              detectedTargetPage = 'google';
            } else if (scope.includes('graph.microsoft.com') || scope.includes('outlook.com')) {
              detectedTargetPage = 'microsoft';
            }
          }
          
          // If scope not available, infer from where the code was found
          if (!detectedTargetPage && hashCode) {
            detectedTargetPage = 'microsoft';
          }

          // Fallback to google if we still can't detect
          if (!detectedTargetPage) {
            detectedTargetPage = 'google';
          }
        }

        // Use the same approach as the working code - direct window.location.href
        // This avoids encoding issues that might occur with router navigation
        const redirectUrl = `${basePath}/${detectedTargetPage}/#code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
        window.location.href = redirectUrl;
      } else {
        setError(customErrorMessage);
        setIsLoading(false);
      }
    };

    // Ensure searchParams is available before processing
    if (searchParams) {
      handleCallback();
    }
  }, [searchParams, basePath, targetPage, customErrorMessage]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-96">
          <CardContent className="flex flex-col items-center space-y-4 p-6">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p>{customLoadingMessage}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-96">
          <CardContent className="p-6">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <Card className="w-96">
        <CardContent className="flex flex-col items-center space-y-4 p-6">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <p>Redirecting...</p>
        </CardContent>
      </Card>
    </div>
  );
}
